import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoPersonCircleOutline } from "react-icons/io5";
import SubscriptionChangeDate from "./SubcriptionChangeDate";
import SubscriptionGiftDetails from "./SubscriptionGiftDetails";
import SubManageContact from "./SubManageContact";
import { formatCustomDate } from "../../helpers/formatCustomDate";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";

const Field = ({ label, children, align = "left" }) => (
  <div className={`flex gap-2 py-1 ${align === "right" ? "justify-end" : ""}`}>
    <span className="font-medium text-gray-600 shrink-0">{label}:</span>
    <span className="font-semibold text-gray-800 break-words">{children || <span className="text-gray-300">—</span>}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-lg font-semibold text-blue-600 mb-3 pb-2 border-b border-gray-100">{children}</h3>
);

function SubOverview(props) {
  const { subDetails, itemList, viewCustomer, updateBillingDate } = props;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];

  useEffect(() => { }, [props?.subDetails?.Subscription_Ref_No]);

  function viewSalesOrder(subscription) {
    if (subDetails?.Sales_Order_Ref_No) {
      navigate(
        `/${currModuleId}/${currModuleName}/sales-order/details/${subDetails?.Sales_Order_Ref_No}`
      );
    }
  }

  const getSubTotal = (list) => {
    let temp = 0;
    list?.forEach((product) => {
      temp += product.rate * product.quantity;
    });
    return temp;
  };

  const subTotal = getSubTotal(itemList);
  const discount = subDetails?.discount_amount || 0;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-h-[calc(100vh-80px)] overflow-y-auto">
      {/* Profile Section */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer Details */}
          <div className="flex items-start gap-4">
            <IoPersonCircleOutline className="w-16 h-16 sm:w-20 sm:h-20 text-blue-600 shrink-0" />
            <div className="text-sm min-w-0">
              <div className="flex gap-2 py-1">
                <span className="font-medium text-gray-600">Name:</span>
                <button
                  onClick={() => viewCustomer(subDetails?.customer_id)}
                  className="font-semibold text-blue-600 hover:underline text-left"
                >
                  {subDetails?.customer_full_name}
                </button>
              </div>
              <Field label="Email">{subDetails?.customer_email_address}</Field>
              <Field label="Mobile">{subDetails?.customer_mobile_no}</Field>
              <div className="flex items-center gap-2 py-1">
                <span className="font-medium text-gray-600">Subscription Status:</span>
                <span
                  className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold ${subDetails?.subscription_status_id === 2
                    ? "bg-gray-100 text-gray-700"
                    : "bg-green-100 text-green-600"
                    }`}
                >
                  {subDetails?.subscription_status_name}
                </span>
              </div>
              <Field label="Subscription Type">{subDetails?.recuring_description}</Field>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="text-sm md:text-right">
            <SectionTitle>Subscription Details</SectionTitle>
            <Field label="Subscription ID" align="right">{subDetails?.subscription_ref_no}</Field>
            <Field label="Subscription No" align="right">{subDetails?.subscription_no}</Field>
            <Field label="Sales Person" align="right">{subDetails?.gift_name}</Field>
            <Field label="Created by" align="right">{subDetails?.encoded_by_name}</Field>
            {subDetails?.gift_id === 1 && (
              <div className="flex md:justify-end mt-2">
                <SubscriptionGiftDetails giftPerson={subDetails} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Billing and Shipping Address */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <SectionTitle>Billing Address</SectionTitle>
            <Field label="Country">{subDetails?.billing_country_name}</Field>
            <Field label="State">{subDetails?.billing_city_name}</Field>
            <Field label="Address">{subDetails?.billing_address1}</Field>
          </div>
          <div>
            <SectionTitle>Shipping Address</SectionTitle>
            <Field label="Country">{subDetails?.shipping_country_name}</Field>
            <Field label="State">{subDetails?.shipping_city_name}</Field>
            <Field label="Address">{subDetails?.shipping_address1}</Field>
          </div>
          <div>
            <SectionTitle>Associated Contacts</SectionTitle>
            <p className="text-gray-800 py-1">noreply@grocedy.com</p>
            <SubManageContact contactsList={subDetails?.contacts} />
          </div>
        </div>

        {/* Customer Note */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-100 border-l-4 border-l-blue-600 rounded-md text-sm">
          <h3 className="text-base font-semibold text-blue-600 mb-1">Customer Note</h3>
          {subDetails?.Customer_Notes ? (
            <p className="font-semibold text-gray-800 whitespace-pre-line break-words">
              {subDetails?.Customer_Notes}
            </p>
          ) : (
            <p className="text-gray-400 italic">No customer note</p>
          )}
        </div>
      </section>

      {/* Subscription Payment Details */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <Field label="Payment Term">{subDetails?.payment_term_name}</Field>
          <div className="md:text-right">
            <Field label="Subscription Start Date" align="right">
              {formatCustomDate(subDetails?.subscription_activated_date_string)}
            </Field>
          </div>
        </div>
      </section>

      {/* Subscription Amount and Billing */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm md:divide-x divide-gray-100">
          <div>
            <p className="text-base font-semibold text-gray-700">Subscription Amount</p>
            <p className="text-lg font-bold text-gray-800">
              ₦{formatCustomNumber(subDetails?.subscription_amount)}
            </p>
          </div>
          <div className="md:pl-6">
            <p className="text-base font-semibold text-gray-700">Next Billing Date</p>
            <p className="text-lg font-bold text-gray-800">
              {formatCustomDate(subDetails?.next_bill_on)}
            </p>
            <SubscriptionChangeDate updateBillingDate={updateBillingDate} />
          </div>
          <div className="md:pl-6">
            <p className="text-base font-semibold text-gray-700">Last Billing Date</p>
            <p className="text-lg font-bold text-gray-800">
              {formatCustomDate(subDetails?.last_billed_on)}
            </p>
          </div>
        </div>
      </section>

      {/* Plan & Addon Details */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="py-3 px-4 font-semibold">Item Name</th>
                <th className="py-3 px-4 font-semibold">Sku</th>
                <th className="py-3 px-4 font-semibold text-right">Qty</th>
                <th className="py-3 px-4 font-semibold text-right">Rate</th>
                <th className="py-3 px-4 font-semibold text-right">Tax</th>
                <th className="py-3 px-4 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {itemList?.map((product, i) => (
                <tr
                  key={i}
                  className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => viewSalesOrder(product)}
                >
                  <td className="py-3 px-4 font-semibold">{product?.item_name}</td>
                  <td className="py-3 px-4">{product?.item_sku}</td>
                  <td className="py-3 px-4 text-right">{product?.quantity}</td>
                  <td className="py-3 px-4 text-right">₦{formatCustomNumber(product?.rate)}</td>
                  <td className="py-3 px-4 text-right">{product?.Tax_Id}</td>
                  <td className="py-3 px-4 text-right font-semibold">₦{formatCustomNumber(product?.total_amount)}</td>
                </tr>
              ))}
              {!itemList?.length && (
                <tr className="border-t border-gray-200">
                  <td colSpan={6} className="py-6 text-center text-gray-400">No items</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-4">
          <div className="w-full sm:w-72 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">SubTotal</span>
              <span className="font-semibold text-gray-800">₦{formatCustomNumber(subTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Coupon Discount</span>
              <span className="font-semibold text-gray-800">₦{formatCustomNumber(discount)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-800">Net</span>
              <span className="font-bold text-gray-900">₦{formatCustomNumber(subTotal - discount)}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SubOverview;
